import {ColumnDef} from '@tanstack/react-table';
import {OrganizationMember} from '@tinct/core-api-models';
import {Avatar, AvatarFallback, AvatarImage, Badge, Tooltip, TooltipContent, TooltipTrigger} from '@tinct/web-components';
import {CollectionColumnHeader, CollectionRowActions,} from '@tinct/web-collection';
import {format} from 'date-fns';
import {IconBan, IconCircleCheck, IconKey, IconShieldCheck, IconShieldOff} from '@tabler/icons-react';
import {FolderSyncIcon, Trash, UserCog} from "lucide-react";
import {getMemberActionRestrictions, getMemberStatusDisplay} from '../member-directory-state';

const roleVariants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  ADMIN: 'default',
  MEMBER: 'secondary',
  OWNER: 'default',
  VIEWER: 'secondary',
  EDITOR: 'secondary',
};

const mfaVariants: Record<'on' | 'off', 'success' | 'warning'> = {
  on: 'success',
  off: 'warning',
};


export function SsoLinkedCell({ member }: { member: Pick<OrganizationMember, 'ssoLinked'> }) {
  if (!member.ssoLinked) return <span>-</span>;
  return (
    <Badge variant="important">
      <IconKey />
      SSO
    </Badge>
  );
}

export function MemberStatusCell({ member }: { member: Pick<OrganizationMember, 'status' | 'scimManaged'> }) {
  const display = getMemberStatusDisplay(member);
  if (!display) return <span>-</span>;
  return (
    <div className="flex items-center gap-1.5 bg-muted px-2 py-0.5 rounded-md w-fit">
      <div className={`size-2 rounded-full ${display.dotClassName}`} />
      <span className="text-xs font-medium">{display.label}</span>
    </div>
  );
}

export function DirectoryManagedCell({ member }: { member: Pick<OrganizationMember, 'scimManaged'> }) {
  if (!member.scimManaged) return <span>-</span>;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge variant="outline">
          <FolderSyncIcon />
          Directory
        </Badge>
      </TooltipTrigger>
      <TooltipContent>Managed by your identity provider</TooltipContent>
    </Tooltip>
  );
}

type UseTeamMembersColumnsOptions = {
  organizationId: string;
  /**
   * The workspace's stored SCIM switch. Only while it is on does the directory own its members'
   * life cycle and mapped roles; the Directory badge and status label show regardless.
   */
  scimEnabled?: boolean;
  onChangeRole?: (member: OrganizationMember) => void;
  onSuspendMember?: (member: OrganizationMember) => void;
  onUnsuspendMember?: (member: OrganizationMember) => void;
  onRemoveMember?: (member: OrganizationMember) => void;
};

export function useTeamMembersColumns(
  options: UseTeamMembersColumnsOptions,
): ColumnDef<OrganizationMember>[] {
  const { scimEnabled = false, onChangeRole, onSuspendMember, onUnsuspendMember, onRemoveMember } = options;

  return [
    {
      id: 'email',
      accessorKey: 'email',
      header: ({ column }) => (
        <CollectionColumnHeader column={column} title="Member" />
      ),
      cell: ({ row }) => {
        const { email, user } = row.original;
        const { firstName, lastName } = user || row.original;
        const fullName = firstName || lastName
          ? `${firstName ?? ''} ${lastName ?? ''}`.trim()
          : null;
        const initials = fullName
          ? fullName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
          : email?.slice(0, 2).toUpperCase() ?? '??';

        return (
          <div className="flex items-center gap-2">
            <Avatar className="size-6">
              <AvatarImage src={user?.profileImage?.url ?? undefined} />
              <AvatarFallback className="text-xs">{initials}</AvatarFallback>
            </Avatar>
            <div className="flex flex-col">
              <span className="text-sm font-semibold">{fullName ?? email}</span>
              {fullName && (
                <span className="text-xs text-muted-foreground">{email}</span>
              )}
            </div>
          </div>
        );
      },
      meta: { className: 'min-w-[200px]' },
    },
    {
      accessorKey: 'role',
      header: ({ column }) => (
        <CollectionColumnHeader column={column} title="Role" />
      ),
      cell: ({ row }) => {
        const role = row.original.role;
        if (!role) return <span>-</span>;
        return (
          <Badge variant={roleVariants[role] ?? 'secondary'} className="capitalize">
            {role.toLowerCase()}
          </Badge>
        );
      },
      filterFn: 'equals',
      enableSorting: true,
    },
    {
      accessorKey: 'status',
      header: ({ column }) => (
        <CollectionColumnHeader column={column} title="Status" />
      ),
      // A member the directory deactivated is `suspended` for the API: `scimManaged` tells them apart
      cell: ({ row }) => <MemberStatusCell member={row.original} />,
      filterFn: 'equals',
      enableSorting: true,
    },
    {
      accessorKey: 'createdAt',
      header: ({ column }) => (
        <CollectionColumnHeader column={column} title="Joined" />
      ),
      cell: ({ row }) => (
        <div className="text-sm">
          {row.getValue('createdAt')
            ? format(row.getValue('createdAt'), 'dd/MM/yyyy')
            : '-'}
        </div>
      ),
      enableSorting: true,
    },
    {
      accessorKey: 'mfaEnabled',
      header: () => <span>2FA</span>,
      // The API fills this only for active admins of the workspace; everyone else gets null.
      cell: ({ row }) => {
        const { mfaEnabled, status } = row.original;
        if (mfaEnabled == null || status === 'invited') return <span>-</span>;
        return mfaEnabled ? (
          <Badge variant={mfaVariants.on}>
            <IconShieldCheck />
            On
          </Badge>
        ) : (
          <Badge variant={mfaVariants.off}>
            <IconShieldOff />
            Off
          </Badge>
        );
      },
      enableSorting: false,
    },
    {
      accessorKey: 'ssoLinked',
      header: () => <span>SSO</span>,
      // Filled by the API for the workspace's admins only (null otherwise). A member who never
      // signed in through the connection has no badge: false says nothing about how they sign in.
      cell: ({ row }) => <SsoLinkedCell member={row.original} />,
      enableSorting: false,
    },
    {
      accessorKey: 'scimManaged',
      header: () => <span>Directory</span>,
      // Filled by the API for viewers who may read the SCIM settings (null otherwise)
      cell: ({ row }) => <DirectoryManagedCell member={row.original} />,
      enableSorting: false,
    },
    {
      id: 'actions',
      header: () => <div className="text-right">Actions</div>,
      cell: ({ row }) => {
        const restrictions = getMemberActionRestrictions(row.original, scimEnabled);

        return <div className="flex items-center justify-end gap-1">
          <CollectionRowActions
            row={row}
            actions={[
              {
                label: 'Change role',
                icon: UserCog,
                disabledReason: restrictions.changeRoleDisabledReason,
                onClick: (member: OrganizationMember) => {
                  onChangeRole?.(member);
                },
              },
              restrictions.suspension === 'unsuspend'
                ? {
                    label: 'Restore access',
                    icon: IconCircleCheck,
                    disabledReason: restrictions.suspensionDisabledReason,
                    onClick: (member: OrganizationMember) => {
                      onUnsuspendMember?.(member);
                    },
                  }
                : {
                    label: 'Suspend access',
                    icon: IconBan,
                    disabledReason: restrictions.suspensionDisabledReason,
                    onClick: (member: OrganizationMember) => {
                      onSuspendMember?.(member);
                    },
                  },
              {
                label: 'Remove from workspace',
                icon: Trash,
                group: 'danger',
                className: 'text-red-500!',
                disabledReason: restrictions.removeDisabledReason,
                onClick: (member: OrganizationMember) => {
                  onRemoveMember?.(member);
                },
              },
            ]}
          />
        </div>
      }
    },
  ];
}
